import { transformCodeToDate } from '@/functions/utils';
import { useDataStore } from '@/stores/useDataStore';
import { useHistorycalStore } from '@/stores/useHistorycalStore';
import { calculateAgeInDays, roundFloat, transformDateToCode } from './utils';

export const metricOptions = (t: (key: string) => string) => [
  { value: 'temperature', label: t('temperature') },
  { value: 'ph', label: t('ph') },
];

// Initialiser les dates à partir de selectedCulture
export const initializeDates = () => {
  const dataStore = useDataStore()
  const historyStore = useHistorycalStore()

  if (!dataStore.selectedCulture) {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    historyStore.startDate = sevenDaysAgo.toISOString().slice(0, 16);
    historyStore.endDate = new Date().toISOString().slice(0, 16);
    return;
  }

  // Start date
  const startdayCode = dataStore.selectedCulture.startdaycode?.startday_code;
  if (!startdayCode) {
    console.warn('initializeDates: startdaycode missing, using fallback');
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    historyStore.startDate = sevenDaysAgo.toISOString().slice(0, 16);
  } else {
    const startStr = transformCodeToDate(startdayCode);
    if (startStr === "Code invalide") {
      console.warn('initializeDates: Invalid startday_code, using fallback');
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      historyStore.startDate = sevenDaysAgo.toISOString().slice(0, 16);
    } else {
      const start = new Date(startStr);
      if (isNaN(start.getTime())) {
        console.warn('initializeDates: Invalid start date, using fallback');
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        historyStore.startDate = sevenDaysAgo.toISOString().slice(0, 16);
      } else {
        historyStore.startDate = start.toISOString().slice(0, 16);
      }
    }
  }

  // End date
  const prelevements = dataStore.selectedCulture.prelevements || [];
  if (!prelevements.length) {
    console.warn('initializeDates: prelevements empty, using fallback');
    const defaultEnd = new Date(historyStore.startDate);
    defaultEnd.setDate(defaultEnd.getDate() + 7);
    historyStore.endDate = defaultEnd.toISOString().slice(0, 16);
  } else {
    // Sort prelevements by daycode in ascending order
    const sortedPrelevements = prelevements
      .slice() // Create a copy to avoid mutating original
      .sort((a, b) => a.daycode.localeCompare(b.daycode));
    const lastPrelevement = sortedPrelevements[sortedPrelevements.length - 1];
    if (!lastPrelevement?.daycode) {
      console.warn('initializeDates: No daycode in last prelevement, using fallback');
      const defaultEnd = new Date(historyStore.startDate);
      defaultEnd.setDate(defaultEnd.getDate() + 7);
      historyStore.endDate = defaultEnd.toISOString().slice(0, 16);
    } else {
      const endStr = transformCodeToDate(lastPrelevement.daycode);
      if (endStr === "Code invalide") {
        console.warn('initializeDates: Invalid daycode in last prelevement, using fallback');
        const defaultEnd = new Date(historyStore.startDate);
        defaultEnd.setDate(defaultEnd.getDate() + 7);
        historyStore.endDate = defaultEnd.toISOString().slice(0, 16);
      } else {
        const end = new Date(endStr);
        if (isNaN(end.getTime())) {
          console.warn('initializeDates: Invalid end date, using fallback');
          const defaultEnd = new Date(historyStore.startDate);
          defaultEnd.setDate(defaultEnd.getDate() + 7);
          historyStore.endDate = defaultEnd.toISOString().slice(0, 16);
        } else {
          historyStore.endDate = end.toISOString().slice(0, 16);
        }
      }
    }
  }
};

// Fonction pour filtrer les items historiques par métrique
export function filterByMetric(metric: string, historicalData: HistoricalDataPoint[]): HistoricalDataPoint[] {
  return historicalData.filter(item => {
    if (metric === 'temperature' && item._measurement.includes('Temp')) return true;
    if (metric === 'ph' && item._measurement.includes('pH')) return true;
    return false;
  });
}

// Fonction pour filtrer les outliers
function detectOutliers(items: HistoricalDataPoint[], metric: string): { cleanItems: HistoricalDataPoint[]; ignored: { time: string; value: number }[] } {
  const historyStore = useHistorycalStore();

  const ignored: { time: string; value: number }[] = [];
  const cleanItems = items.filter(item => {
    const v = item._value;

    // Vérification des seuils pour la température
    if (metric === 'temperature') {
      const isOutlier = v < historyStore.temperatureMin || v > historyStore.temperatureMax;
      if (isOutlier) ignored.push({ time: item._time, value: v });
      return !isOutlier;
    }

    // Vérification des seuils pour le pH
    if (metric === 'ph') {
      const isOutlier = v < historyStore.phMin || v > historyStore.phMax;
      if (isOutlier) ignored.push({ time: item._time, value: v });
      return !isOutlier;
    }

    return true;
  });

  return { cleanItems, ignored };
}

// Fonction pour agréger les données d'une métrique (moyenne, min, max par jour)
export function getMetricAggregatedData(
  metric: string,
  historicalData: HistoricalDataPoint[],
  startdaycode: string,
  includeRange: boolean = false,
  includeOutliers: boolean = true
): { averageData: MetricPoint[]; rangeData: MetricPoint[]; ignoredOutliers: { time: string; value: number }[] } {
  let items = filterByMetric(metric, historicalData);
  let ignoredOutliers: { time: string; value: number }[] = [];

  if (!includeOutliers) {
    const { cleanItems, ignored } = detectOutliers(items, metric);
    items = cleanItems;
    ignoredOutliers = ignored;
  }

  const aggregated = items.reduce((acc: Record<string, { date: string; values: number[]; min: number; max: number; age: number }>, item) => {
    const time = new Date(item._time);
    const dateStr = time.toISOString().slice(0, 10);
    const dayCode = transformDateToCode(dateStr);
    if (dayCode === 'Code invalide') return acc;
    const age = calculateAgeInDays(dayCode, startdaycode);
    if (isNaN(age)) return acc;

    if (!acc[dateStr]) {
      acc[dateStr] = { date: dateStr, values: [], min: Infinity, max: -Infinity, age };
    }
    acc[dateStr].values.push(item._value);
    acc[dateStr].min = Math.min(acc[dateStr].min, item._value);
    acc[dateStr].max = Math.max(acc[dateStr].max, item._value);
    return acc;
  }, {});

  const averageData: MetricPoint[] = Object.values(aggregated)
    .map(entry => {
      let avg = entry.values.length > 0 ? entry.values.reduce((sum, val) => sum + val, 0) / entry.values.length : 0;
      let minVal = entry.min;
      let maxVal = entry.max;
      const roundedAvg = roundFloat(avg);
      const numericAvg = typeof roundedAvg === 'string' ? parseFloat(roundedAvg) : roundedAvg;
      if (isNaN(numericAvg)) return null;
      return { x: entry.age, y: numericAvg, min: minVal, max: maxVal, avg: numericAvg };
    })
    .filter((point): point is MetricPoint => point !== null && Number.isFinite(point.y))
    .sort((a, b) => a.x - b.x);

  let rangeData: MetricPoint[] = [];
  if (includeRange) {
    rangeData = Object.values(aggregated)
      .flatMap(entry => {
        let minVal = entry.values.length > 0 ? entry.min : 0;
        let maxVal = entry.values.length > 0 ? entry.max : 0;
        let avgVal = entry.values.length > 0 ? entry.values.reduce((sum, val) => sum + val, 0) / entry.values.length : 0;
        const roundedMin = roundFloat(minVal);
        const roundedMax = roundFloat(maxVal);
        const roundedAvg = roundFloat(avgVal);
        const numericMin = typeof roundedMin === 'string' ? parseFloat(roundedMin) : roundedMin;
        const numericMax = typeof roundedMax === 'string' ? parseFloat(roundedMax) : roundedMax;
        const numericAvg = typeof roundedAvg === 'string' ? parseFloat(roundedAvg) : roundedAvg;
        if (isNaN(numericMin) || isNaN(numericMax)) return [];
        return [
          { x: entry.age, y: numericMin, isMin: true, min: numericMin, max: numericMax, avg: numericAvg },
          { x: entry.age, y: numericMax, isMin: false, min: numericMin, max: numericMax, avg: numericAvg }
        ];
      })
      .sort((a, b) => {
        if (a.x === b.x) return a.isMin ? -1 : 1;
        return a.x - b.x;
      });
  }

  return { averageData, rangeData, ignoredOutliers };
}

// Fonction pour obtenir les données d'absorbance (sans outliers)
export function getAbsorbanceData(
  startdaycode: string,
  prelevements: Prelevement[],
  volumeUtilePBR: number | undefined
): ChartPoint[] {
  const sortedPrelevements = prelevements
    .filter(
      (p): p is Prelevement & { absorbance_880nm: number } =>
        p.absorbance_880nm != null && !isNaN(p.absorbance_880nm)
    )
    .sort((a, b) => a.daycode.localeCompare(b.daycode));

  return sortedPrelevements
    .flatMap((p) => {
      const age = Number(calculateAgeInDays(p.daycode, startdaycode));
      if (isNaN(age)) return [];

      const points: ChartPoint[] = [{ x: age, y: p.absorbance_880nm }];

      if (
        p.vol_recolte != null &&
        volumeUtilePBR != null &&
        !isNaN(p.vol_recolte) &&
        !isNaN(volumeUtilePBR)
      ) {
        const newValue = roundFloat(
          p.absorbance_880nm * (volumeUtilePBR - p.vol_recolte) / volumeUtilePBR
        );
        const numericNewValue =
          typeof newValue === 'string' ? parseFloat(newValue) : newValue;
        if (!isNaN(numericNewValue)) {
          points.push({ x: age, y: numericNewValue });
        }
      }

      return points;
    })
    .sort((a, b) => a.x - b.x);
}

// Fonction pour grouper les données pour la liste (utilisée dans HistoricalData_dataList.vue)
export function getGroupedDataForList(
  historicalData: HistoricalDataPoint[],
): GroupedHistoricalData[] {
  const groups: Record<string, GroupedHistoricalData> = {};

  historicalData.forEach(item => {
    let groupKey = '';
    if (item._measurement.includes('Temp')) groupKey = 'temperature';
    else if (item._measurement.includes('pH')) groupKey = 'ph';
    else return;

    if (!groups[groupKey]) {
      groups[groupKey] = { label: groupKey, values: [] };
    }

    const suffix = item._measurement.match(/_(A|B)$/)?.[1] || '';
    const unit = groupKey === 'temperature' ? 'C°' : '';

    let value = item._value;
    let displayValue: string | number = value;
    groups[groupKey].values.push({
      time: item._time,
      value,
      displayValue,
      suffix: suffix || undefined,
      unit: unit || undefined,
    });
  });

  return Object.values(groups);
}